import { useState } from "react";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogTitle,
} from "@/components/ui/dialog";
import { ScanText, ZoomIn } from "lucide-react";

/**
 * Galleria sola-lettura delle foto sorgente OCR del rapportino (i cartacei
 * originali scansionati dall'AI). Click su una thumbnail per ingrandire.
 *
 * Props:
 *  - sources: [{storage_path, original_filename, created_at}]
 *  - apiBase: REACT_APP_BACKEND_URL
 */
export default function OcrSourceGallery({ sources = [], apiBase }) {
  const [zoom, setZoom] = useState(null);
  if (!sources || sources.length === 0) return null;

  const url = (p) => `${apiBase}/api/files/${encodeURIComponent(p)}`;

  return (
    <Card className="p-4 md:p-5 space-y-3 border-amber-200 bg-amber-50/40">
      <div className="font-heading font-bold tracking-tight flex items-center gap-2">
        <ScanText className="h-5 w-5 text-amber-700" /> Sorgente cartacea (OCR)
        <span className="text-xs font-normal text-slate-500">
          ({sources.length} pagin{sources.length === 1 ? "a" : "e"} originale —
          consultabile per verifica)
        </span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
        {sources.map((s, i) => (
          <button
            type="button"
            key={s.storage_path}
            onClick={() => setZoom(s)}
            className="relative group aspect-[3/4] bg-white border border-amber-200 rounded overflow-hidden hover:ring-2 hover:ring-amber-400 transition"
            data-testid={`ocr-source-thumb-${i}`}
            title={`Pagina ${i + 1}${s.original_filename ? ` · ${s.original_filename}` : ""}`}
          >
            <img
              src={url(s.storage_path)}
              alt={`Pagina ${i + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 grid place-items-center transition">
              <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100" />
            </div>
            <div className="absolute bottom-0 inset-x-0 text-[10px] bg-black/60 text-white py-0.5 text-center font-bold">
              {i + 1}
            </div>
          </button>
        ))}
      </div>

      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-5xl p-3 md:p-4">
          <DialogTitle className="text-base font-heading font-bold">
            Sorgente cartacea
            {zoom?.original_filename ? ` · ${zoom.original_filename}` : ""}
          </DialogTitle>
          {zoom && (
            <div className="bg-slate-900 rounded overflow-auto max-h-[80vh] flex items-center justify-center">
              <img
                src={url(zoom.storage_path)}
                alt="Sorgente cartacea"
                className="max-w-full max-h-[78vh] object-contain"
                data-testid="ocr-source-zoom-img"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
